import React, { useState, useMemo } from "react";
import { StringParam, useQueryParams } from "use-query-params";
import { TruthyNumberArrayParam } from "@src/lib/utils/queryParams";
import { gql } from "@apollo/client";
import PaginatedTable from "@src/lib/ui/PaginatedTable";
import { Button } from "@src/lib/ui/Button";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from "@src/lib/ui/EmptyState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/lib/ui/Select";
import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";
import {
  TABLE_TOOLBAR_CHEVRON_DOWN_ICON_CLASSNAME,
  TABLE_TOOLBAR_BUTTON_CLASSNAME,
} from "@src/lib/utils/tableToolbarChrome";
import { cn } from "@src/lib/utils/cn";
import { format, subDays } from "date-fns";
import { useDebouncedQueryParam } from "@src/lib/hooks/useDebouncedQueryParam";
import Text from "@src/lib/ui/Text";
import TopThemesRow from "./TopThemesSidebar";
import GuestFeedbackRatingTrendChart from "./GuestFeedbackRatingTrendChart";
import { Card, CardContent } from "@src/lib/ui/Card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@src/lib/ui/Sheet";
import Spinner from "@src/lib/ui/Spinner";
import {
  useGuestFeedbackReviewsTab_GuestFeedbacksQuery,
  useGuestFeedbackReviewsTab_GuestFeedbacksLazyQuery,
  GuestFeedbackReviewsTab_GuestFeedbackFragment,
} from "./GuestFeedbackReviewsTab.generated";
import useGuestFeedbackSetupStatus from "./hooks/useGuestFeedbackSetupStatus";
import { useOrgGuestFeedbackSyncsQuery } from "./GuestFeedbackSyncsTab.generated";
import {
  useGuestFeedbackCsvImport,
  CSV_IMPORT_POLL_INTERVAL_MS,
} from "./useGuestFeedbackCsvImport";
import { useRecommendationsMockData } from "@src/features/recommendations/RecommendationsMockDataContext";
import {
  getMockGuestFeedbacksData,
  getMockLocationOptions,
} from "./recommendationsMockData";
import ReportingFilterDropdown, {
  ReportingFilterOption,
} from "@src/features/filters/ui/ReportingFilterDropdown";
import useLocations from "@src/features/locations/hooks/useLocations";
import PinsOutlineIcon from "@src/lib/icons/18px/pins-outline";

// Refresh cadence for the reviews list while the tab is open. We poll faster
// while waiting for an integration's first sync, then back off once data lands.
const POLL_INTERVAL_MS = 60_000;
const SYNCING_POLL_INTERVAL_MS = 15_000;
// Right after a CSV upload the import runs server-side, so we poll aggressively
// for a short window to surface the new reviews as soon as they land.

type ReviewColumn = "date" | "rating" | "source" | "location" | "review";

const REVIEW_COLUMNS: ReviewColumn[] = [
  "date",
  "rating",
  "source",
  "location",
  "review",
];

const REVIEW_COLUMN_TITLES: Record<ReviewColumn, string> = {
  date: "Date",
  rating: "Rating",
  source: "Source",
  location: "Location",
  review: "Review",
};

const StarRating: React.FC<{ rating: number | null | undefined }> = ({
  rating,
}) => {
  if (rating == null)
    return (
      <Text variant="fine-print-description" className="text-muted-foreground">
        —
      </Text>
    );
  const rounded = Math.round(rating);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className={cn(
            "text-sm",
            i < rounded ? "text-yellow-7" : "text-gray-3",
          )}
        >
          ★
        </span>
      ))}
      <Text
        variant="fine-print-description"
        className="ml-1 text-muted-foreground"
      >
        {rating.toFixed(1)}
      </Text>
    </div>
  );
};

function getFeedbackLocation(
  feedback: GuestFeedbackReviewsTab_GuestFeedbackFragment,
): string {
  const mappedName =
    feedback.integrationLocation?.location?.name ??
    feedback.csvUploadLocation?.location?.name;
  if (mappedName) return mappedName;
  // Integration locations that haven't been mapped to an Opus location fall
  // back to the provider's listing name, which for multi-location brands is
  // often just the business name (e.g. every Google listing titled "Hair
  // Saloon") — flag it so it isn't mistaken for a real location.
  if (feedback.integrationLocation?.sourceName) {
    return `${feedback.integrationLocation.sourceName} (unmapped)`;
  }
  return feedback.csvUploadLocation?.locationName ?? "—";
}

// feedbackSource resolves to the CSV upload's source label or, for integration
// feedback, the provider name (e.g. "Ovation" / "Google Business"). CSV-sourced
// feedback gets a "(CSV)" suffix so a custom label like "Ovation" stays
// distinguishable from actual Ovation / Google Business integration feedback.
function getFeedbackSource(
  feedback: GuestFeedbackReviewsTab_GuestFeedbackFragment,
): string {
  if (!feedback.feedbackSource) return "—";
  if (feedback.csvUploadLocation) return `${feedback.feedbackSource} (CSV)`;
  return feedback.feedbackSource;
}

// Mock-aware wrappers around the reviews query hooks: PaginatedTable owns the
// query calls, so when the staff mock-data flag is on we skip Apollo and
// synthesize the response (filters, sort, and pagination included) from the
// recommendations mock data set.
const useGuestFeedbacksQueryMaybeMock: typeof useGuestFeedbackReviewsTab_GuestFeedbacksQuery =
  (baseOptions) => {
    const { useMockData } = useRecommendationsMockData();
    // The cast keeps the generated hook's required-variables union happy: the
    // table always passes variables, and when mocking we skip anyway.
    const result = useGuestFeedbackReviewsTab_GuestFeedbacksQuery({
      ...baseOptions,
      skip: useMockData || baseOptions?.skip,
    } as Parameters<typeof useGuestFeedbackReviewsTab_GuestFeedbacksQuery>[0]);
    // Key off the serialized variables so mock data keeps a stable identity
    // across renders (the caller builds the variables object inline).
    const variablesKey = JSON.stringify(baseOptions?.variables ?? null);
    const mockData = useMemo(() => {
      if (!useMockData) return undefined;
      const variables = JSON.parse(variablesKey) as {
        input?: Parameters<typeof getMockGuestFeedbacksData>[0];
        pagination?: Parameters<typeof getMockGuestFeedbacksData>[1];
      } | null;
      return getMockGuestFeedbacksData(variables?.input, variables?.pagination);
    }, [useMockData, variablesKey]);
    return useMockData
      ? { ...result, data: mockData, loading: false, error: undefined }
      : result;
  };

const useGuestFeedbacksLazyQueryMaybeMock: typeof useGuestFeedbackReviewsTab_GuestFeedbacksLazyQuery =
  (baseOptions) => {
    const { useMockData } = useRecommendationsMockData();
    const [fetch, result] =
      useGuestFeedbackReviewsTab_GuestFeedbacksLazyQuery(baseOptions);
    const variablesKey = JSON.stringify(baseOptions?.variables ?? null);
    const mockFetch = React.useCallback(
      async (options?: Parameters<typeof fetch>[0]) => {
        const base = JSON.parse(variablesKey) as {
          input?: Parameters<typeof getMockGuestFeedbacksData>[0];
        } | null;
        // The table only reads `.data` from the fetch-all result, so a partial
        // QueryResult is safe here.
        return {
          data: getMockGuestFeedbacksData(
            options?.variables?.input ?? base?.input,
            undefined,
          ),
        } as Awaited<ReturnType<typeof fetch>>;
      },
      [variablesKey],
    );
    return useMockData ? [mockFetch as typeof fetch, result] : [fetch, result];
  };

const GuestFeedbackReviewsTab: React.FC = () => {
  const { useMockData } = useRecommendationsMockData();
  const [queryParams, setQueryParams] = useQueryParams({
    q: StringParam,
    datePreset: StringParam,
    minRating: StringParam,
    maxRating: StringParam,
    themeId: StringParam,
    locationIds: TruthyNumberArrayParam,
  });

  const { datePreset, minRating, maxRating, themeId } = queryParams;
  const locationIds = useMemo(
    () => queryParams.locationIds ?? [],
    [queryParams.locationIds],
  );

  // Search is owned by the table's built-in search box (the ?q param). We read
  // the debounced value here so the top-themes row and trend chart refetch in
  // step with the table, without a second search input.
  const { debouncedValue: debouncedSearch } = useDebouncedQueryParam({
    queryParamName: "q",
    initialValue: "",
    delay: 300,
  });

  // The date filter is a rolling-window preset (last N days). We derive the
  // concrete range from the selected preset so there's a single source of truth.
  const { startDate, endDate } = useMemo(() => {
    const days = datePreset ? Number(datePreset) : NaN;
    if (!Number.isFinite(days)) {
      return {
        startDate: undefined as Date | undefined,
        endDate: undefined as Date | undefined,
      };
    }
    return { startDate: subDays(new Date(), days), endDate: new Date() };
  }, [datePreset]);

  const [selectedFeedback, setSelectedFeedback] =
    useState<GuestFeedbackReviewsTab_GuestFeedbackFragment | null>(null);

  const parsedMinRating = minRating ? Number(minRating) : undefined;
  const parsedMaxRating = maxRating ? Number(maxRating) : undefined;

  // The table injects `search` (from ?q) and `sort` (from ?sort) into the query
  // input itself, so we only supply the remaining filters here.
  const input = useMemo(
    () => ({
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      minRating: parsedMinRating,
      maxRating: parsedMaxRating,
      themeIds: themeId ? [themeId] : undefined,
      locationIds: locationIds.length > 0 ? locationIds : undefined,
    }),
    [
      startDate,
      endDate,
      parsedMinRating,
      parsedMaxRating,
      themeId,
      locationIds,
    ],
  );

  // A sync is in progress when an integration has a run that started but hasn't
  // completed yet (the backend has no explicit status — an unset completedAt is
  // the in-flight signal). We use this to show a banner and poll faster.
  const { data: syncsData } = useOrgGuestFeedbackSyncsQuery({
    skip: useMockData,
  });
  const isSyncInProgress =
    !useMockData &&
    (syncsData?.OrgGuestFeedbackSyncs?.objects ?? []).some(
      (sync) => !sync.completedAt,
    );

  const handleSelectTheme = (nextThemeId: string | null) => {
    setQueryParams({ themeId: nextThemeId ?? undefined });
  };

  // Mock reviews are attached to synthesized locations, so the dropdown has to
  // offer those instead of the org's real ones while the staff toggle is on.
  const { locations } = useLocations();
  const locationOptions = useMemo<ReportingFilterOption[]>(
    () =>
      (useMockData ? getMockLocationOptions() : locations).map((location) => ({
        label: location.name,
        value: location.id.toString(),
        type: "Location",
      })),
    [locations, useMockData],
  );

  const hasFilters =
    !!debouncedSearch ||
    !!datePreset ||
    !!minRating ||
    !!maxRating ||
    !!themeId ||
    locationIds.length > 0;

  const { isCsvImportInProgress } = useGuestFeedbackCsvImport();

  const pollInterval = isCsvImportInProgress
    ? CSV_IMPORT_POLL_INTERVAL_MS
    : isSyncInProgress
      ? SYNCING_POLL_INTERVAL_MS
      : POLL_INTERVAL_MS;

  const { loading: setupLoading, hasAnyReviews } = useGuestFeedbackSetupStatus({
    pollInterval,
  });

  const emptyState = (
    <EmptyState>
      <EmptyStateTitle>No reviews found</EmptyStateTitle>
      <EmptyStateDescription>
        {hasFilters
          ? "Try adjusting your filters."
          : "Guest feedback reviews will appear here once data is imported."}
      </EmptyStateDescription>
    </EmptyState>
  );

  const filterControls = (
    <div className="flex flex-wrap items-center gap-2">
      <ReportingFilterDropdown
        options={locationOptions}
        selectedValues={locationIds.map(String)}
        onSelectionChange={(values) =>
          setQueryParams({
            locationIds: values.length > 0 ? values.map(Number) : undefined,
          })
        }
        placeholder="All locations"
        searchPlaceholder="Search locations..."
        noResultsText="No locations found."
        LeadingIcon={PinsOutlineIcon}
      />
      <Select
        value={datePreset ?? "all"}
        onValueChange={(v) => {
          setQueryParams({ datePreset: v === "all" ? undefined : v });
        }}
      >
        <SelectTrigger asChild>
          <Button
            variant="secondary"
            size="md"
            className={cn(
              TABLE_TOOLBAR_BUTTON_CLASSNAME,
              "w-[140px] justify-between font-medium",
            )}
          >
            <SelectValue placeholder="All dates" />
            <ChevronDownIcon
              className={TABLE_TOOLBAR_CHEVRON_DOWN_ICON_CLASSNAME}
            />
          </Button>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All dates</SelectItem>
          <SelectItem value="30">Last 30 days</SelectItem>
          <SelectItem value="60">Last 60 days</SelectItem>
          <SelectItem value="90">Last 90 days</SelectItem>
        </SelectContent>
      </Select>
      <div className="flex items-center gap-2">
        <Select
          value={minRating ?? ""}
          onValueChange={(v) => {
            setQueryParams({ minRating: v || undefined });
          }}
        >
          <SelectTrigger asChild>
            <Button
              variant="secondary"
              size="md"
              className={cn(
                TABLE_TOOLBAR_BUTTON_CLASSNAME,
                "w-[120px] justify-between font-medium",
              )}
            >
              <SelectValue placeholder="Min rating" />
              <ChevronDownIcon
                className={TABLE_TOOLBAR_CHEVRON_DOWN_ICON_CLASSNAME}
              />
            </Button>
          </SelectTrigger>
          <SelectContent>
            {[1, 2, 3, 4, 5].map((r) => (
              <SelectItem key={r} value={String(r)}>
                {r}★
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Text
          variant="fine-print-description"
          className="text-muted-foreground"
        >
          to
        </Text>
        <Select
          value={maxRating ?? ""}
          onValueChange={(v) => {
            setQueryParams({ maxRating: v || undefined });
          }}
        >
          <SelectTrigger asChild>
            <Button
              variant="secondary"
              size="md"
              className={cn(
                TABLE_TOOLBAR_BUTTON_CLASSNAME,
                "w-[120px] justify-between font-medium",
              )}
            >
              <SelectValue placeholder="Max rating" />
              <ChevronDownIcon
                className={TABLE_TOOLBAR_CHEVRON_DOWN_ICON_CLASSNAME}
              />
            </Button>
          </SelectTrigger>
          <SelectContent>
            {[1, 2, 3, 4, 5].map((r) => (
              <SelectItem key={r} value={String(r)}>
                {r}★
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            setQueryParams({
              q: undefined,
              minRating: undefined,
              maxRating: undefined,
              datePreset: undefined,
              themeId: undefined,
              locationIds: undefined,
            })
          }
        >
          Reset
        </Button>
      )}
    </div>
  );

  const subHeader = (
    <div className="flex flex-col gap-4 py-2">
      <TopThemesRow
        selectedThemeId={themeId}
        onSelectTheme={handleSelectTheme}
        search={debouncedSearch || undefined}
        startDate={startDate}
        endDate={endDate}
        minRating={parsedMinRating}
        maxRating={parsedMaxRating}
        locationIds={locationIds}
      />
      <Card variant="elevated">
        <CardContent className="p-4">
          <GuestFeedbackRatingTrendChart
            compact
            themeId={themeId}
            locationIds={locationIds}
          />
        </CardContent>
      </Card>
    </div>
  );

  if (setupLoading) {
    return (
      <div className="flex w-full min-w-0 flex-1 flex-col py-4">
        <EmptyState loading />
      </div>
    );
  }

  if (!hasAnyReviews) {
    return (
      <div className="flex w-full min-w-0 flex-1 flex-col py-4">
        <EmptyState>
          {isSyncInProgress ? (
            <>
              <Spinner className="size-6 text-muted-foreground" />
              <EmptyStateTitle>Syncing your reviews</EmptyStateTitle>
              <EmptyStateDescription>
                Your connected integration is importing guest feedback. Reviews
                will appear here automatically once the first sync finishes —
                this can take a few minutes.
              </EmptyStateDescription>
            </>
          ) : (
            <>
              <EmptyStateTitle>No reviews found</EmptyStateTitle>
              <EmptyStateDescription>
                Guest feedback reviews will appear here once data is imported.
              </EmptyStateDescription>
            </>
          )}
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-4 overflow-hidden py-4">
      {isSyncInProgress && (
        <div className="flex items-center gap-3 rounded-xl border border-blue-3 bg-blue-1 px-4 py-3">
          <Spinner size={20} className="shrink-0 text-blue-9" />
          <Text
            variant="fine-print-description"
            multiline
            className="text-blue-11"
          >
            <span className="font-semibold">Syncing reviews…</span> A connected
            integration is importing guest feedback. New reviews will appear
            here automatically as they come in.
          </Text>
        </div>
      )}
      <PaginatedTable<
        GuestFeedbackReviewsTab_GuestFeedbackFragment,
        ReviewColumn,
        typeof useGuestFeedbackReviewsTab_GuestFeedbacksQuery,
        typeof useGuestFeedbackReviewsTab_GuestFeedbacksLazyQuery,
        ReviewColumn
      >
        title="Reviews"
        allItemsCountInput={{}}
        rightAction={filterControls}
        SubHeader={subHeader}
        queryHook={useGuestFeedbacksQueryMaybeMock}
        lazyQueryHook={useGuestFeedbacksLazyQueryMaybeMock}
        getItems={(x) => x?.GuestFeedbacks.objects ?? []}
        getTotalItemCount={(x) => x?.GuestFeedbacks.totalCount ?? 0}
        itemId={(x) => x.id}
        input={input}
        pollInterval={pollInterval}
        onRowClick={(x) => setSelectedFeedback(x)}
        sidebarSelectedRowId={selectedFeedback?.id ?? undefined}
        allColumns={REVIEW_COLUMNS}
        columnTitles={REVIEW_COLUMN_TITLES}
        columnSelection={{
          enabled: true,
          alwaysVisibleColumns: ["date", "review"],
          defaultSelectedColumns: REVIEW_COLUMNS,
        }}
        defaultSort={{ column: "date", descending: true }}
        disabledSorts={["source", "location", "review"]}
        noDataEmptyState={emptyState}
        horizontalScroll
        resizableColumns
        columnWidthPersistenceKey="guest-feedback-reviews-v3"
        trailingEmptyColumnWidth={16}
        growableColumn="review"
        responsiveness={{
          date: { minWidth: 96, width: 120 },
          rating: { minWidth: 120, width: 150 },
          source: { minWidth: 120, width: 150 },
          location: { minWidth: 140, width: 200 },
          review: { minWidth: 280 },
        }}
        renderFns={{
          date: (fb) => format(new Date(fb.date), "M/d/yyyy"),
          rating: (fb) => <StarRating rating={fb.rating} />,
          source: (fb) => getFeedbackSource(fb),
          location: (fb) => getFeedbackLocation(fb),
          review: (fb) => fb.text ?? "—",
        }}
      />
      <ReviewDetailSheet
        feedback={selectedFeedback ?? undefined}
        open={Boolean(selectedFeedback)}
        onOpenChange={(open) => {
          if (!open) setSelectedFeedback(null);
        }}
      />
    </div>
  );
};

const ReviewDetailSheet: React.FC<{
  feedback: GuestFeedbackReviewsTab_GuestFeedbackFragment | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ feedback, open, onOpenChange }) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[440px] sm:max-w-[440px]">
        {feedback && (
          <div className="flex flex-col gap-6">
            <SheetHeader className="pr-6">
              <SheetTitle>Guest review</SheetTitle>
              <SheetDescription>
                {format(new Date(feedback.date), "MMM d, yyyy")} ·{" "}
                {getFeedbackLocation(feedback)}
              </SheetDescription>
            </SheetHeader>

            <div className="flex flex-col gap-2">
              <Text variant="fine-print-title" className="text-gray-8">
                Rating
              </Text>
              <StarRating rating={feedback.rating} />
            </div>

            <div className="flex flex-col gap-2">
              <Text variant="fine-print-title" className="text-gray-8">
                Review
              </Text>
              <Text
                variant="compact-description"
                multiline
                className="text-foreground"
              >
                {feedback.text ?? "No review text provided."}
              </Text>
            </div>

            <div className="grid grid-cols-2 gap-3 rounded-xl border border-gray-2 bg-gray-1 p-4">
              <div className="flex flex-col gap-1">
                <Text
                  variant="fine-print-description"
                  className="text-muted-foreground"
                >
                  Source
                </Text>
                <Text variant="fine-print-title">
                  {getFeedbackSource(feedback)}
                </Text>
              </div>
              <div className="flex flex-col gap-1">
                <Text
                  variant="fine-print-description"
                  className="text-muted-foreground"
                >
                  Location
                </Text>
                <Text variant="fine-print-title">
                  {getFeedbackLocation(feedback)}
                </Text>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

gql`
  fragment GuestFeedbackReviewsTab_GuestFeedback on GuestFeedback {
    id
    text
    date
    rating
    feedbackSource
    integrationLocation {
      id
      sourceName
      sourceLocationId
      location {
        id
        name
      }
    }
    csvUploadLocation {
      id
      locationName
      location {
        id
        name
      }
    }
  }

  query GuestFeedbackReviewsTab_GuestFeedbacks(
    $input: GuestFeedbacksInput!
    $pagination: PaginationInput
  ) {
    GuestFeedbacks(input: $input, pagination: $pagination) {
      objects {
        ...GuestFeedbackReviewsTab_GuestFeedback
      }
      totalCount
    }
  }
`;

export default GuestFeedbackReviewsTab;
